import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useApi } from '../../lib/api';
import { useWorkspace } from '../../lib/workspace';
import Toggle from '../../components/shared/Toggle';
import { PROJECT_MAIL_TYPES } from '../../lib/projectMailTypes';
import { SearchableSelect } from '../../components/workspace/filters/FilterDropdowns';
import DocumentCategoriesSettings from '../../components/workspace/documents/DocumentCategoriesSettings';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const MAX_SNAPSHOT_CC = 2;
const MAX_EMAIL_LENGTH = 254; // RFC 5321 max total length for an email address

export default function WorkspaceSettings() {
  const { appId } = useParams();
  const api = useApi();
  const { canWrite } = useWorkspace();

  const [roles, setRoles] = useState([]);
  const [defaultApproverId, setDefaultApproverId] = useState('');
  const [minHoursPerDay, setMinHoursPerDay] = useState('');
  const [minHoursPerWeek, setMinHoursPerWeek] = useState('');
  const [monthlyWorkingDays, setMonthlyWorkingDays] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');

  const [mailSettings, setMailSettings] = useState(null);
  const [mailError, setMailError] = useState('');

  // Daily snapshot CC list (up to 2 emails) — also saves instantly.
  const [newCcEmail, setNewCcEmail] = useState('');
  const [ccSaving, setCcSaving] = useState(false);
  const [ccError, setCcError] = useState('');

  useEffect(() => {
    loadData();
  }, [appId]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [settingsData, rolesData, mailData] = await Promise.all([
        api.getPerformanceSettings(appId),
        api.roles.list(appId),
        api.getProjectMailControl(appId),
      ]);
      setDefaultApproverId(settingsData.default_final_approver_role_id || '');
      setMinHoursPerDay(settingsData.min_hours_per_day ?? '');
      setMinHoursPerWeek(settingsData.min_hours_per_week ?? '');
      setMonthlyWorkingDays(settingsData.monthly_working_days ?? 22);
      setRoles(rolesData.items || []);
      setMailSettings(mailData);
    } catch {
      setError('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  // Mail Control toggles save immediately (one field at a time), with
  // optimistic UI and rollback on failure — separate from the performance
  // settings form's explicit Save button below.
  const handleMailToggle = async (key, value) => {
    const previous = mailSettings;
    setMailSettings((s) => ({ ...s, [key]: value }));
    setMailError('');
    try {
      const updated = await api.updateProjectMailControl(appId, { [key]: value });
      setMailSettings(updated);
    } catch (err) {
      setMailSettings(previous);
      setMailError(err.response?.data?.error || 'Failed to update mail settings');
    }
  };

  const saveSnapshotCc = async (nextList) => {
    const previous = mailSettings;
    setMailSettings((s) => ({ ...s, mail_daily_snapshot_cc: nextList }));
    setCcSaving(true);
    setCcError('');
    try {
      const updated = await api.updateProjectMailControl(appId, { mail_daily_snapshot_cc: nextList });
      setMailSettings(updated);
      return true;
    } catch (err) {
      setMailSettings(previous);
      setCcError(err.response?.data?.error || 'Failed to update CC recipients');
      return false;
    } finally {
      setCcSaving(false);
    }
  };

  const handleAddSnapshotCc = async (e) => {
    e.preventDefault();
    const email = newCcEmail.trim().toLowerCase();
    setCcError('');
    if (!email) return;
    if (email.length > MAX_EMAIL_LENGTH) { setCcError(`That email is too long (max ${MAX_EMAIL_LENGTH} characters)`); return; }
    if (!EMAIL_RE.test(email)) { setCcError('Enter a valid email address, e.g. name@company.com'); return; }
    const current = mailSettings.mail_daily_snapshot_cc || [];
    if (current.length >= MAX_SNAPSHOT_CC) { setCcError(`You can add at most ${MAX_SNAPSHOT_CC} CC recipients`); return; }
    if (current.includes(email)) { setCcError('That email is already in the list'); return; }
    if (await saveSnapshotCc([...current, email])) setNewCcEmail('');
  };

  const handleSave = async () => {
    setSaving(true);
    setSaved(false);
    setError('');
    try {
      await api.updatePerformanceSettings(appId, {
        default_final_approver_role_id: defaultApproverId ? parseInt(defaultApproverId) : null,
        min_hours_per_day:      minHoursPerDay      !== '' ? parseFloat(minHoursPerDay)      : 0,
        min_hours_per_week:     minHoursPerWeek     !== '' ? parseFloat(minHoursPerWeek)     : 0,
        monthly_working_days:   monthlyWorkingDays  !== '' ? parseInt(monthlyWorkingDays)    : 22,
      });
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="loading">Loading settings…</div>;

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap', marginBottom: 20 }}>
        <div>
          <h2 style={{ margin: '0 0 4px', fontSize: 18, color: 'var(--text-primary)' }}>Performance Settings</h2>
          <p style={{ margin: 0, color: 'var(--text-secondary)', fontSize: 14 }}>
            Configure default settings for performance reviews and goal approvals.
          </p>
        </div>
        {canWrite && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            {saved && <span style={{ color: 'var(--pos)', fontSize: 14 }}>✓ Saved</span>}
            <button className="btn-primary" onClick={handleSave} disabled={saving}>
              {saving ? 'Saving…' : 'Save Settings'}
            </button>
          </div>
        )}
      </div>

      {/* Row 1 — Final Approver | Working Hours | Routine Settings (saved by Save Settings) */}
      <div style={rowStyle}>
      <div style={cardStyle}>
        <h3 style={sectionHeadStyle}>Final Approver (HR Manager)</h3>
        <p style={hintStyle}>
          The role selected here will be the default final approver for all review cycles.
          This can be overridden per cycle when creating or editing a review cycle.
        </p>

        <label style={labelStyle}>Default Final Approver Role</label>
        <div style={{ marginBottom: 6 }}>
          <SearchableSelect
            value={defaultApproverId}
            onChange={v => setDefaultApproverId(v)}
            options={[{ value: '', label: '— None (admin decides per cycle) —' }, ...roles.map(role => ({ value: role.id, label: role.title }))]}
            showAll={false}
            disabled={!canWrite || saving}
            btnClass="form-select-btn"
            matchTriggerWidth
          />
        </div>
        <p style={hintStyle}>
          Person assigned to this role will perform the final approval or rejection of performance reviews.
        </p>
      </div>

      <div style={cardStyle}>
        <h3 style={sectionHeadStyle}>Working Hours</h3>
        <p style={hintStyle}>
          Set the minimum expected working hours per day and per week. These are used to flag
          under-utilisation in the team dashboard and to suggest realistic task deadlines.
        </p>

        <div style={{ display: 'flex', gap: 16, marginTop: 8 }}>
          <div style={{ flex: 1 }}>
            <label style={labelStyle}>Minimum Hours per Day</label>
            <input
              type="number"
              className="form-input"
              min="0"
              max="24"
              step="0.5"
              value={minHoursPerDay}
              onChange={e => setMinHoursPerDay(e.target.value)}
              disabled={!canWrite || saving}
              placeholder="e.g. 6"
              style={{ width: '100%', boxSizing: 'border-box' }}
            />
            <p style={hintStyle}>Hours an employee is expected to work each day.</p>
          </div>
          <div style={{ flex: 1 }}>
            <label style={labelStyle}>Minimum Hours per Week</label>
            <input
              type="number"
              className="form-input"
              min="0"
              max="168"
              step="0.5"
              value={minHoursPerWeek}
              onChange={e => setMinHoursPerWeek(e.target.value)}
              disabled={!canWrite || saving}
              placeholder="e.g. 40"
              style={{ width: '100%', boxSizing: 'border-box' }}
            />
            <p style={hintStyle}>Hours an employee is expected to work each week.</p>
          </div>
        </div>
      </div>

      <div style={cardStyle}>
        <h3 style={sectionHeadStyle}>Routine Settings</h3>
        <p style={hintStyle}>
          Define the number of working days in a month. This is used to calculate the expected
          completion count for daily routines (e.g. 1 task per day × 22 working days).
        </p>
        <div style={{ maxWidth: 200, marginTop: 8 }}>
          <label style={labelStyle}>Monthly Working Days</label>
          <input
            type="number"
            className="form-input"
            min="1"
            max="31"
            step="1"
            value={monthlyWorkingDays}
            onChange={e => setMonthlyWorkingDays(e.target.value)}
            disabled={!canWrite || saving}
            placeholder="e.g. 22"
            style={{ width: '100%', boxSizing: 'border-box' }}
          />
          <p style={hintStyle}>Typically 22 for a standard month (excluding weekends).</p>
        </div>
      </div>
      </div>

      {error && <div className="error-message" style={{ marginTop: 12 }}>{error}</div>}

      {/* Row 2 — Mail Control | Document Categories (each saves on its own) */}
      <div style={{ ...rowStyle, marginTop: 24 }}>
      {mailSettings && (
        <div style={cardStyle}>
          <h3 style={sectionHeadStyle}>Mail Control</h3>
          <p style={hintStyle}>
            Turn workspace emails on or off. Project emails to clients apply to every
            project — a project's own Mail Settings can only narrow this further, never override
            it. Every toggle below saves instantly — there's no separate Save button for this section.
          </p>

          <div style={{
            marginTop: 16, paddingTop: 14, paddingBottom: 14, borderTop: '1px solid var(--border)',
            display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16,
          }}>
            <div>
              <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-primary)' }}>GLOS Daily Snapshot</div>
              <div style={{ fontSize: 12.5, color: 'var(--text-secondary)', marginTop: 2 }}>
                A summary of the previous day across the whole workspace, emailed to the app owner at 2 AM.
              </div>
            </div>
            <Toggle
              checked={mailSettings.mail_daily_snapshot}
              disabled={!canWrite}
              onChange={(v) => handleMailToggle('mail_daily_snapshot', v)}
            />
          </div>

          {mailSettings.mail_daily_snapshot && (
            <div style={{ paddingLeft: 4, paddingBottom: 14 }}>
              <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' }}>CC</span>
              <p style={{ fontSize: 12, color: 'var(--text-secondary)', margin: '2px 0 10px', lineHeight: 1.5 }}>
                Up to {MAX_SNAPSHOT_CC} more email addresses that get a copy of the snapshot, alongside the app owner.
              </p>

              {(mailSettings.mail_daily_snapshot_cc || []).length > 0 && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: canWrite ? 10 : 0 }}>
                  {mailSettings.mail_daily_snapshot_cc.map((email) => (
                    <div key={email} style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                      padding: '5px 10px', background: 'var(--bg-muted)',
                      border: '1px solid var(--border)', borderRadius: 7, fontSize: 12.5,
                    }}>
                      <span style={{ color: 'var(--text-primary)' }}>{email}</span>
                      {canWrite && (
                        <button
                          onClick={() => saveSnapshotCc(mailSettings.mail_daily_snapshot_cc.filter((e) => e !== email))}
                          disabled={ccSaving}
                          title="Remove"
                          style={{
                            background: 'none', border: 'none', cursor: ccSaving ? 'wait' : 'pointer',
                            color: 'var(--text-muted)', fontSize: 13, padding: '0 2px', lineHeight: 1,
                          }}
                        >
                          ✕
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {canWrite && (mailSettings.mail_daily_snapshot_cc || []).length < MAX_SNAPSHOT_CC && (
                <form onSubmit={handleAddSnapshotCc} style={{ display: 'flex', gap: 8 }}>
                  <input
                    type="email"
                    className="form-input"
                    placeholder="name@company.com"
                    value={newCcEmail}
                    onChange={(e) => setNewCcEmail(e.target.value)}
                    disabled={ccSaving}
                    maxLength={MAX_EMAIL_LENGTH}
                    style={{ margin: 0, flex: 1, fontSize: 12.5 }}
                  />
                  <button
                    type="submit"
                    className="btn-secondary"
                    disabled={ccSaving || !newCcEmail.trim()}
                    style={{ padding: '4px 14px', fontSize: 12.5, flexShrink: 0 }}
                  >
                    {ccSaving ? '…' : '+ Add'}
                  </button>
                </form>
              )}

              {ccError && <div className="form-error" style={{ marginTop: 8 }}>{ccError}</div>}
            </div>
          )}

          <div style={{
            marginTop: 16, paddingTop: 14, paddingBottom: 14, borderTop: '1px solid var(--border)',
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          }}>
            <span style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-primary)' }}>Project Mail</span>
            <Toggle
              checked={mailSettings.project_mail_enabled}
              disabled={!canWrite}
              onChange={(v) => handleMailToggle('project_mail_enabled', v)}
            />
          </div>

          <div style={{ paddingLeft: 4, display: 'flex', flexDirection: 'column', gap: 12 }}>
            {PROJECT_MAIL_TYPES.map((t) => (
              <div key={t.key} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <span style={{ fontSize: 13, color: 'var(--text-primary)' }}>{t.label}</span>
                <Toggle
                  checked={mailSettings[t.key]}
                  disabled={!canWrite || !mailSettings.project_mail_enabled}
                  onChange={(v) => handleMailToggle(t.key, v)}
                />
              </div>
            ))}
          </div>

          {mailError && <div className="error-message" style={{ marginTop: 12 }}>{mailError}</div>}
        </div>
      )}

      <DocumentCategoriesSettings
        appId={appId} canWrite={canWrite}
        cardStyle={cardStyle} sectionHeadStyle={sectionHeadStyle} hintStyle={hintStyle}
      />
      </div>
    </div>
  );
}

// Cards sit side by side and wrap to fewer columns on narrow screens
const rowStyle = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
  gap: 16,
  alignItems: 'start',
};

const cardStyle = {
  background: 'var(--bg-card)',
  border: '1px solid var(--border)',
  borderRadius: 8,
  padding: '20px 24px',
};

const sectionHeadStyle = {
  margin: '0 0 6px',
  fontSize: 15,
  fontWeight: 600,
  color: 'var(--text-primary)',
};

const labelStyle = {
  display: 'block',
  fontWeight: 600,
  fontSize: 13,
  color: 'var(--text-primary)',
  marginBottom: 6,
  marginTop: 16,
};

const hintStyle = {
  margin: '4px 0 0',
  fontSize: 12,
  color: 'var(--text-secondary)',
  lineHeight: 1.5,
};
